import { useEffect, useState, useRef } from "react";
import { ChargeArtisanResponse, ChargeArtisanCell, SectionKey } from "types/planning";
import { planningsApi } from "@services/api/plannings";
import { CHARGE_ARTISAN_SECTION_KEYS } from "../../../utils/planning_constants";

interface Props {
  orgId: string;
}

export const ChargeArtisanTable = ({ orgId }: Props) => {
  const [sectionKey, setSectionKey] = useState<SectionKey>(
    CHARGE_ARTISAN_SECTION_KEYS[0].key as SectionKey
  );
  const [data, setData] = useState<ChargeArtisanResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [popup, setPopup] = useState<{
    cells: ChargeArtisanCell[];
    x: number;
    y: number;
  } | null>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setData(null);
    setLoading(true);
    planningsApi
      .getChargeArtisan({ org_id: orgId, section_key: sectionKey })
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [orgId, sectionKey]);

  // Close popup on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(e.target as Node)) {
        setPopup(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  function handleCellClick(
    cells: ChargeArtisanCell[],
    e: React.MouseEvent
  ) {
    setPopup({ cells, x: e.clientX, y: e.clientY });
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Section selector */}
      <div className="flex flex-wrap gap-2">
        {CHARGE_ARTISAN_SECTION_KEYS.map((s) => (
          <button
            key={s.key}
            onClick={() => setSectionKey(s.key as SectionKey)}
            className={`px-3 py-1 rounded-full text-h3 border transition-all ${
              sectionKey === s.key
                ? "bg-neutral-900 text-white border-neutral-900"
                : "bg-white text-neutral-700 border-neutral-300 hover:border-neutral-500"
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {loading && (
        <div className="text-h3 text-neutral-400 py-4">Chargement...</div>
      )}

      {!loading && data && (
        <div className="overflow-x-auto rounded-xl border border-neutral-200">
          <table className="border-collapse w-full">
            <thead>
              <tr>
                <th
                  className="bg-neutral-100 border border-neutral-200 px-3 py-2 text-h4 text-left sticky left-0 z-10"
                  style={{ minWidth: 180 }}
                >
                  Artisan
                </th>
                {data.iso_weeks.map((w) => (
                  <th
                    key={w}
                    className="bg-neutral-100 border border-neutral-200 px-1 py-2 text-h4 text-center font-medium"
                    style={{ minWidth: 32 }}
                  >
                    S{w}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.artisan_rows.length === 0 && (
                <tr>
                  <td
                    className="px-3 py-4 text-h3 text-neutral-400"
                    colSpan={data.iso_weeks.length + 1}
                  >
                    Aucune charge pour ce corps de métier.
                  </td>
                </tr>
              )}
              {data.artisan_rows.map((row) => (
                <tr key={row.artisan_id}>
                  <td
                    className="bg-white border border-neutral-100 px-3 py-2 text-h3 font-medium sticky left-0 z-10"
                    style={{ minWidth: 180 }}
                  >
                    {row.artisan_name}
                  </td>
                  {data.iso_weeks.map((w) => {
                    const cells = row.cells[w] || [];
                    const count = cells.length;
                    return (
                      <td
                        key={w}
                        className={`border border-neutral-100 text-center text-h3 font-bold transition-colors ${
                          count > 0
                            ? "bg-red-400 text-white cursor-pointer hover:bg-red-500"
                            : "bg-white"
                        }`}
                        onClick={
                          count > 0
                            ? (e) => handleCellClick(cells, e)
                            : undefined
                        }
                        style={{ minWidth: 32, height: 32 }}
                      >
                        {count > 0 ? count : ""}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && data && data.artisan_rows.length === 0 && (
        <div className="text-h3 text-neutral-400">
          Aucun planning actif pour ce corps de métier.
        </div>
      )}

      {/* Popup */}
      {popup && (
        <div
          ref={popupRef}
          className="fixed z-50 bg-white border border-neutral-200 rounded-xl shadow-lg p-4 max-w-xs"
          style={{ top: popup.y + 8, left: popup.x + 8 }}
        >
          <div className="text-h3 font-bold mb-2">Chantiers concernés</div>
          <div className="flex flex-col gap-2">
            {popup.cells.map((cell, i) => (
              <div key={i} className="flex flex-col text-h4">
                <span className="font-medium text-neutral-800">
                  {cell.project_name}
                </span>
                {cell.conductor_name && (
                  <span className="text-neutral-500">
                    CDT : {cell.conductor_name}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
